const DEFAULT_FILENAME = 'questionario.pdf';
const PDF_CONTAINER_CLASS = 'questionario-pdf-container';
const PRINT_TARGET_CLASS = 'questionario-print-target';

let html2pdfLoader;

const loadHtml2Pdf = async () => {
  if (!html2pdfLoader) {
    html2pdfLoader = import('html2pdf.js').then((mod) => mod.default || mod);
  }
  return html2pdfLoader;
};

const sanitizeSegment = (value) => {
  if (!value) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
};

export const buildQuestionarioPdfFileName = ({ prefix = 'questionario', userId, updatedAt } = {}) => {
  const now = new Date();
  const date = updatedAt instanceof Date ? updatedAt : updatedAt ? new Date(updatedAt) : now;
  const dateSegment = Number.isFinite(date?.getTime()) ? date.toISOString().slice(0, 10) : now.toISOString().slice(0, 10);
  const safePrefix = sanitizeSegment(prefix) || 'questionario';
  const safeUser = sanitizeSegment(userId) || 'utente';
  return `${safePrefix}_${safeUser}_${dateSegment}.pdf`;
};

const syncFormValues = (source, clone) => {
  const sourceFields = source.querySelectorAll('input, textarea, select');
  const cloneFields = clone.querySelectorAll('input, textarea, select');
  sourceFields.forEach((field, index) => {
    const cloneField = cloneFields[index];
    if (!cloneField) return;
    if (field instanceof HTMLInputElement) {
      if (field.type === 'checkbox' || field.type === 'radio') {
        cloneField.checked = field.checked;
        return;
      }
      cloneField.value = field.value;
      cloneField.setAttribute('value', field.value);
      return;
    }
    if (field instanceof HTMLTextAreaElement) {
      cloneField.value = field.value;
      cloneField.textContent = field.value;
      return;
    }
    if (field instanceof HTMLSelectElement) {
      cloneField.value = field.value;
      Array.from(cloneField.options).forEach((option) => {
        option.selected = option.value === field.value;
      });
    }
  });
};

const createPdfSource = (element) => {
  const clone = element.cloneNode(true);
  syncFormValues(element, clone);
  clone.classList.add(PRINT_TARGET_CLASS);
  clone.style.width = '100%';
  clone.style.maxWidth = '100%';

  const rect = element.getBoundingClientRect();
  const referenceWidth = Number.isFinite(rect.width) && rect.width > 0 ? rect.width : element.offsetWidth || 900;

  const container = document.createElement('div');
  container.className = PDF_CONTAINER_CLASS;
  Object.assign(container.style, {
    position: 'absolute',
    left: '-99999px',
    top: `${window.scrollY || 0}px`,
    width: `${referenceWidth}px`,
    maxWidth: `${referenceWidth}px`,
    background: '#ffffff',
  });

  container.appendChild(clone);
  document.body.appendChild(container);
  return { container, clone };
};

export const exportElementAsPdf = async (element, { fileName = DEFAULT_FILENAME, pdfOptions = {} } = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!element) throw new Error('Elemento per il PDF non disponibile.');
  const { container, clone } = createPdfSource(element);
  try {
    const html2pdf = await loadHtml2Pdf();
    const deviceScale = window.devicePixelRatio || 1;
    const baseOptions = {
      margin: [12, 12, 18, 12],
      filename: fileName,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: {
        scale: Math.min(Math.max(deviceScale, 1.5), 2.6),
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] },
    };
    await html2pdf()
      .set({
        ...baseOptions,
        ...pdfOptions,
        filename: fileName || baseOptions.filename,
      })
      .from(clone)
      .save();
  } finally {
    container.remove();
  }
};
